import { format } from 'date-fns';
import {
    CheckCircle2,
    Download,
    Eye,
    FileDown,
    FileSignature,
    Link2,
    Loader2,
    Lock,
    MoreHorizontal,
    PenLine,
    Printer,
    RotateCcw,
    Send,
    ShieldCheck,
    TriangleAlert,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import PartyChips from './PartyChips';
import MergePreflight from './MergePreflight';
import { DOC_TYPES, PARTY_LABELS } from '@/lib/agreements/constants';
import { labelForArea } from '@/components/contracts/setup/steps';
import type { InstrumentKeyGap, TemplateVersionGap } from '@/lib/agreements/templateCatalog';
import { INSURANCE_PAGE_CALLED_FOR, INSURANCE_PAGE_DESCRIPTIONS } from '@/lib/agreements/instrument';
import {
    ADVANCE_LOCK_REASON,
    countersignReadyFor,
    mergePreflightShownFor,
    pageGapShownFor,
    siblingSignatureOutstanding,
    witnessLinkReadyFor,
} from './boardSlots';
import type { BoardSlot, SealState } from './boardSlots';

export interface AgreementRowActions {
    /** Opens the send confirmation. On a slot with no document, confirming generates it. */
    onSend: () => void;
    onReview: () => void;
    onCountersign: () => void;
    onWitnessLink: () => void;
    onVoid: () => void;
    /** Asks the edge function for the executed PDF, and re-arms the automatic attempt. */
    onRetrySeal: () => void;
    onDownloadFinal: () => void;
    onExportPdf: () => void;
    onOpenLegacy: () => void;
    /**
     * Where a pre-flight row or a blocked slot sends the admin.
     *
     * THE SECOND ARGUMENT IS THE MERGE FIELD, AND ONLY A PRE-FLIGHT HAS ONE. `slot.blockedStep`
     * below is a step and nothing else — the block is about the slot, not about a field — so that
     * call passes one argument and the hosts that route by field treat its absence as "the step is
     * the whole answer". See `MergePreflight`.
     */
    onDeepLink: (step: string, field?: string) => void;
}

interface AgreementDocumentRowProps {
    slot: BoardSlot;
    /** `agreement_render`'s `missing[]`, once the document exists. */
    missing: string[] | null;
    /** Which action, if any, is in flight for this row. */
    busy: 'send' | 'export' | null;
    /** Where the executed PDF has got to. Only `sealing` may animate. */
    seal: SealState;
    actions: AgreementRowActions;
    /**
     * Set when a newer template version has been published since this document pinned
     * its own. Only ever supplied for a document still in PENDING_REVIEW.
     */
    templateGap?: TemplateVersionGap | null;
    /**
     * Set when this document was generated on a page its subject no longer calls for [D-B57].
     * Only ever supplied for a document still in PENDING_REVIEW, and only on the three insurance
     * pages — see `instrumentGap` in `useContractAgreements`.
     */
    instrumentGap?: InstrumentKeyGap | null;
}

/**
 * One document on the signing board.
 *
 * THE CHIPS ARE THE STATE. There is no local notion of "sent": a document exists or it does
 * not, and once it does the driver can already see it — `get_my_pending_signatures` covers
 * PENDING_REVIEW. So the primary action is always the next lawful step for whoever has not
 * acted, and never a button that only changes what this screen believes.
 */
export default function AgreementDocumentRow({
    slot,
    missing,
    busy,
    seal,
    actions,
    templateGap = null,
    instrumentGap = null,
}: AgreementDocumentRowProps) {
    // THE TYPE STILL ANSWERS FOR THE ICON, THE OPTIONAL BADGE AND THE PARTIES LINE — all three are
    // facts about the ARC rather than about which instrument was published onto it. Only the NAME
    // moved to the slot [D-B45].
    const meta = DOC_TYPES[slot.docType];
    const Icon = meta.icon;
    const doc = slot.document;

    const signatures = doc?.signatures ?? [];
    const counterparties = signatures.filter((signature) => signature.party !== 'COMPANY_REP');

    /**
     * THE CROSS-DOCUMENT TERM IS INSIDE THIS, NOT BESIDE IT [D-B18]. `countersignReadyFor` asks
     * both the seats on this document and the sibling instrument's party, exactly as
     * `agreement_sign` does — so the button below is never offered into an
     * `AGREEMENT_COUNTERSIGN_BLOCKED`.
     */
    const countersignReady = countersignReadyFor(slot);

    /** The sibling's outstanding signature, once this document's own seats are all in. */
    const ownSeatsSigned =
        counterparties.length > 0 &&
        counterparties.every((signature) => signature.status === 'SIGNED');

    /**
     * The sentence for a row held by ANOTHER instrument, in the three places that happens.
     *
     * On the witness agreement it is the whole pre-signature life of the row: the link is withheld —
     * by `witnessLinkReadyFor` from the first paint, and by `blockedByMerge` too once the render
     * reports `contract.hire_purchase_signed_date` missing — and, after the witness signs, the
     * countersignature is refused as well. Naming the merge field there would send an admin to a
     * form; naming the driver sends them to the person.
     *
     * ON EITHER ADVANCE IT APPEARS ONLY ONCE ITS OWN SEATS ARE IN, exactly as on the hire
     * purchase agreement, and for the same reason: before that the row has its own borrower to
     * chase and `advanceLockedFor` has already said what holds it shut. After it, the only
     * signature left is Primelane's and the server refuses it until both contract instruments are
     * executed [D-B27] — which is the state this row used to paint an enabled "Sign as Primelane"
     * into, beside a hire purchase agreement that was correctly saying the witness had to sign.
     *
     * BOTH ADVANCES, BECAUSE THE PREDICATE ANSWERS FOR BOTH [D-B55]. The standing Operational Expense
     * Advance is gated exactly as the premium one is, and a type this list left out does not fall
     * through to a milder sentence — it falls through to "Out for signature.", on a row that is not
     * out for signature, offers no action and gives no reason. The branch body reads the same two
     * facts either way, so it needs nothing per type.
     *
     * IT NAMES THE DRIVER BEFORE THE WITNESS WHEN BOTH ARE OUTSTANDING, which is only reachable on
     * an advance and only after a void-and-reissue of the hire purchase agreement. The order is
     * the dependency's: the witness cannot sign until the driver has, so sending an admin after the
     * witness first would point them at a door that is also shut.
     *
     * AND THE DRIVER'S SENTENCE IS `ADVANCE_LOCK_REASON` RATHER THAN A COPY OF IT: on an advance
     * this row and the padlock `advanceLockedFor` draws are the two ends of ONE dependency, and an
     * admin who reads them minutes apart must not be told the same fact in two wordings.
     *
     * THE WITNESS SENTENCE NEVER REACHES A PAPER-MASTER CONTRACT [D-B53], and it must not: there the
     * waiver has already taken the witness arm off both advances, so `siblingSignatureOutstanding`
     * is false and this whole term is null.
     */
    const crossDocumentBlock =
        slot.docType === 'WITNESS' && siblingSignatureOutstanding(slot)
            ? 'The driver has to sign the hire purchase agreement first.'
            : (slot.docType === 'HIRE_PURCHASE' ||
                slot.docType === 'INSURANCE_LOAN' ||
                slot.docType === 'LOAN') &&
                ownSeatsSigned &&
                siblingSignatureOutstanding(slot)
              ? !slot.driverSigned
                  ? ADVANCE_LOCK_REASON
                  : 'The witness has to sign the witness agreement first.'
              : null;

    const waitingOn = counterparties
        .filter((signature) => signature.status !== 'SIGNED' && signature.status !== 'VOIDED')
        .map((signature) => PARTY_LABELS[signature.party] ?? signature.party);

    const declined = signatures.some((signature) => signature.status === 'DECLINED');
    const blockedByMerge = (missing?.length ?? 0) > 0;
    const scanned = !doc && !!slot.legacySignedAt;
    const completed = doc?.status === 'COMPLETED';
    // Signed by everyone, PDF not stored — three states, not one. Only `sealing` is a
    // request in flight; the other two are waiting on this admin, so both offer the button.
    const sealing = seal.phase === 'sealing';
    const sealFailed = seal.phase === 'failed';
    const readyToSeal = seal.phase === 'ready';
    const awaitingSeal = sealing || sealFailed || readyToSeal;
    /**
     * AND THE SAME TERM IS INSIDE THIS ONE [D-B18]. `witnessLinkReadyFor` asks the sibling
     * instrument's party on the witness agreement exactly as `agreement_issue_token` does, so the
     * button below — and the overflow item that duplicates it — is never offered in the window
     * before `missing[]` arrives, where a click would hit this page's "open Review first" refusal
     * rather than the sentence the status line is already printing.
     */
    const witnessLinkReady = witnessLinkReadyFor(slot, blockedByMerge);
    /** Whether the overflow menu has anything in it. See the trigger below. */
    const hasMenu = !!doc || witnessLinkReady;

    let statusLine: string;
    if (slot.blockedReason) statusLine = slot.blockedReason;
    else if (scanned) statusLine = 'Signed on paper.';
    else if (!doc) statusLine = 'Not generated yet.';
    else if (declined) statusLine = 'A party declined — void and reissue to recover.';
    else if (completed && doc.finalized_at) statusLine = 'All parties signed.';
    else if (sealing) statusLine = 'All parties signed — sealing the executed PDF.';
    else if (sealFailed) statusLine = 'All parties signed — sealing failed.';
    else if (readyToSeal) statusLine = 'All parties signed — ready to seal.';
    else if (countersignReady) statusLine = 'Waiting on your countersignature.';
    else if (crossDocumentBlock) statusLine = crossDocumentBlock;
    // THE PAGE OUTRANKS THE FIELDS [D-B57]. Where a document is on a page its subject no longer
    // calls for, the unresolved merge fields are the SYMPTOM — the weekly ones an advance settled
    // at the end of the contract never had — and a line naming them sends an admin to state money
    // the contract does not owe. It sits behind every line above it because each of those is a
    // more fundamental state of the row, and ahead of the merge line for the same reason.
    else if (instrumentGap) statusLine = 'Generated on the wrong page — void and reissue.';
    else if (blockedByMerge) statusLine = 'Cannot be sent until the merge fields resolve.';
    else if (waitingOn.length) statusLine = `Waiting on ${waitingOn.join(' and ')}.`;
    else statusLine = 'Out for signature.';

    return (
        <article
            className={cn(
                'rounded-xl border p-4 transition-colors',
                completed && doc?.finalized_at && 'border-emerald-500/30 bg-emerald-500/5',
                slot.blockedReason && 'border-destructive/30 bg-destructive/5',
            )}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {/* THE SLOT NAMES ITSELF, THE DOC TYPE DOES NOT [D-B45]. One doc type now
                            publishes two instruments — an Insurance Premium Advance and an
                            Insurance Premium Payment Record — so `meta.label` would call the second
                            one the first on the row an admin opens it from. `slot.title` is the
                            generated document's own pinned title where there is one, and the title
                            the instrument would be generated under where there is not. */}
                        <h3 className="font-medium">{slot.title}</h3>
                        {slot.label && (
                            <span className="text-sm text-muted-foreground">{slot.label}</span>
                        )}
                        {meta.optional && (
                            <Badge variant="outline" className="font-normal">
                                Optional
                            </Badge>
                        )}
                        {scanned && (
                            <Badge variant="outline" className="gap-1 font-normal">
                                <Printer className="h-3 w-3" />
                                Paper
                            </Badge>
                        )}
                    </div>

                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                        {doc && <span className="font-mono">{doc.reference_no}</span>}
                        {doc && <span>·</span>}
                        <span>{meta.description}</span>
                        {slot.subtitle && (
                            <>
                                <span>·</span>
                                <span>{slot.subtitle}</span>
                            </>
                        )}
                    </p>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                    {/* A blocked slot never offers to generate — the deep link is the only
                        thing that can change its answer. */}
                    {slot.blockedReason ? (
                        slot.blockedStep && (
                            <Button size="sm" onClick={() => actions.onDeepLink(slot.blockedStep!)}>
                                Resolve in {labelForArea(slot.blockedStep)}
                            </Button>
                        )
                    ) : !doc && !scanned ? (
                        /* THE LABEL NAMES WHAT THE CLICK DOES, AND ON THE SCHEDULE THAT IS NOT A
                           SEND [D-B48]. Every other row opens `SendForSignatureDialog`, which asks
                           "Send the … for signature?" and dispatches nothing else. The Vehicle
                           Schedule opens a dialog that shows the Cash price it will state, is
                           headed "Generate the …" and confirms with "Generate schedule" — and the
                           contract's cash price is copied at that moment [D-B59], which is the
                           thing the admin is being asked to check. */
                        <Button size="sm" onClick={actions.onSend} disabled={busy === 'send'}>
                            {busy === 'send' ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : slot.docType === 'PARTNER_MANAGEMENT' ? (
                                <FileSignature className="mr-2 h-4 w-4" />
                            ) : (
                                <Send className="mr-2 h-4 w-4" />
                            )}
                            {slot.docType === 'PARTNER_MANAGEMENT'
                                ? 'Generate schedule'
                                : 'Send for signature'}
                        </Button>
                    ) : countersignReady ? (
                        <Button size="sm" onClick={actions.onCountersign}>
                            <PenLine className="mr-2 h-4 w-4" />
                            Sign as Primelane
                        </Button>
                    ) : awaitingSeal ? (
                        // One button across all three seal states, so the row is always
                        // resolvable by hand however the automatic attempt ended.
                        <Button
                            size="sm"
                            variant={sealFailed ? 'default' : 'outline'}
                            onClick={actions.onRetrySeal}
                            disabled={sealing}
                        >
                            {sealing ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <ShieldCheck className="mr-2 h-4 w-4" />
                            )}
                            {sealing ? 'Sealing' : 'Retry seal'}
                        </Button>
                    ) : witnessLinkReady ? (
                        <Button size="sm" onClick={actions.onWitnessLink}>
                            <Link2 className="mr-2 h-4 w-4" />
                            Witness link
                        </Button>
                    ) : null}

                    {doc && (
                        <Button variant="ghost" size="sm" onClick={actions.onReview}>
                            <Eye className="mr-2 h-4 w-4" />
                            Review
                        </Button>
                    )}

                    {/* NO TRIGGER WITHOUT AN ITEM BEHIND IT. Every entry below is gated on a
                        document — `witnessLinkReady` is too, since `witnessLinkReadyFor` reads the
                        seat off `slot.document` — so a row with no document opened a bordered empty
                        popover holding nothing a keyboard user could focus. The paper-filing item
                        was the last unconditional entry [D-B49], and it is the ungenerated row that
                        every one of the twelve backfill schedules starts on. */}
                    {hasMenu && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`More actions for ${slot.title}`}
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                                {witnessLinkReady && (
                                    <DropdownMenuItem onClick={actions.onWitnessLink}>
                                        <Link2 className="mr-2 h-4 w-4" />
                                        Copy witness signing link
                                    </DropdownMenuItem>
                                )}
                                {doc && (
                                    <DropdownMenuItem
                                        onClick={actions.onExportPdf}
                                        disabled={busy === 'export'}
                                    >
                                        <FileDown className="mr-2 h-4 w-4" />
                                        Export PDF
                                    </DropdownMenuItem>
                                )}
                                {doc?.finalized_at && (
                                    <DropdownMenuItem onClick={actions.onDownloadFinal}>
                                        <Download className="mr-2 h-4 w-4" />
                                        Download executed PDF
                                    </DropdownMenuItem>
                                )}
                                {doc && <DropdownMenuSeparator />}
                                {doc && (
                                    <DropdownMenuItem
                                        onClick={actions.onVoid}
                                        className="text-destructive focus:text-destructive"
                                    >
                                        <RotateCcw className="mr-2 h-4 w-4" />
                                        Void &amp; reissue
                                    </DropdownMenuItem>
                                )}
                                {/* NO PAPER ROUTE IS OFFERED ON ANY ARC [D-B49]. `Record paper signing`
                                    and `Replace paper record` both wrote `signed_at`, `agreement_url`
                                    and `origin` onto the legacy row directly, and `authenticated` no
                                    longer holds INSERT or UPDATE on those three columns anywhere — so
                                    the item is removed rather than left to answer a raw `42501` naming
                                    a relation. Reading a scan already on file is untouched: the `View
                                    scan` link below is a SELECT, and the back catalogue it serves is
                                    exactly the population this ruling leaves standing. */}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            {/* FULL WIDTH FROM HERE DOWN. The actions sit inline at the top right, exactly as the
                deposit agreement arranges them, so the chips, the notices and the completed strip
                all span the card — which is what lets the chips sit side by side rather than
                stacking, and what keeps the download on one line with its sentence. */}
            {doc && (
                <PartyChips
                    signatures={signatures}
                    since={doc.frozen_at ?? doc.created_at}
                    className="mt-3"
                />
            )}

            {doc?.frozen_at && !completed && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    Document locked · content frozen{' '}
                    {format(new Date(doc.frozen_at), 'MMM d, yyyy · HH:mm')}
                </p>
            )}

            {scanned && (
                <p className="mt-2 text-sm text-muted-foreground">
                    Signed{' '}
                    {slot.legacySignedAt
                        ? format(new Date(slot.legacySignedAt), 'MMM d, yyyy')
                        : ''}
                    {slot.legacyUrl && (
                        <>
                            {' · '}
                            <button
                                onClick={actions.onOpenLegacy}
                                className="text-primary hover:underline"
                            >
                                View scan
                            </button>
                        </>
                    )}
                </p>
            )}

            {/* THE BLOCK AND THE PAGE GAP BOTH OUTRANK THE PRE-FLIGHT, in the render exactly as
                they do in the status line above — see `mergePreflightShownFor`, which is that
                precedence and is where its cases are pinned. A slot held shut by `blockedReason` is
                being told the FIRST thing that has to happen, and a document on the wrong page is
                being told the fields will resolve themselves on the right one; either way a red
                "cannot be sent — 3 merge fields are still empty" names the symptom over the cause,
                and on the page gap its deep links would change the money to suit a document.

                THE LOCK OUTRANKS THE PAGE GAP IN TURN, and that chain is `pageGapShownFor` beside
                it — the three boxes below are one ladder, not three independent conditions. */}
            {mergePreflightShownFor(slot, { blockedByMerge, instrumentGap: !!instrumentGap }) && (
                <MergePreflight missing={missing ?? []} onDeepLink={actions.onDeepLink} />
            )}

            {/* AND THIS STANDS IN ITS PLACE — A HINT, NOT A BLOCK [D-B57]. Nothing about the send
                moves: `blockedByMerge` goes on gating the witness link and the countersignature
                exactly as it did, and this adds no gate of its own — so in the reverse direction, an
                at-end document on a loan that has since been given a weekly, every merge key
                resolves and the row is as sendable as it was before the hint existed.

                IT NAMES THE PAGES IN `agreement_sign`'s WORDS, IN BOTH HALVES AND IN ITS TWO
                VOCABULARIES: the pinned page in full, the page now called for in the shorter form —
                which is what stops the sentence naming the two advances identically, since they
                share a title. The same admin meets the server's own `AGREEMENT_INSTRUMENT_MISMATCH`
                refusal at the next signature and the two have to read as one sentence in two
                places. Neither says how the hire purchase agreement was executed: that is the
                derivation, not the arrangement the driver signs. */}
            {pageGapShownFor(slot, !!instrumentGap) && instrumentGap && (
                <p className="mt-3 flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                    <TriangleAlert aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        This document was generated as{' '}
                        {INSURANCE_PAGE_DESCRIPTIONS[instrumentGap.pinnedKey] ??
                            instrumentGap.pinnedTitle}
                        , and the insurance premium behind it now calls for{' '}
                        {INSURANCE_PAGE_CALLED_FOR[instrumentGap.expectedKey] ??
                            instrumentGap.expectedTitle}
                        . Documents keep the page they were generated on — use{' '}
                        <span className="font-medium">Void &amp; reissue</span> to generate it on
                        the right one.
                    </span>
                </p>
            )}

            {/* A HINT, NOT A BLOCK. The document is perfectly valid on the version it
                pinned; the newer wording only reaches it by voiding this one and issuing
                a replacement, which is an existing overflow action rather than a new one.

                AND IT YIELDS TO THE PAGE GAP [D-B57], which is the more fundamental problem and
                carries the same remedy. `newerPublishedVersion` compares versions WITHIN the pinned
                key, so on a document that is also on the wrong page its sentence promises an outcome
                the reissue will not produce: `agreement_create` selects by KEY, so the replacement
                lands on the EXPECTED key's published row and never on the newer version of the
                pinned one. Two amber boxes both ending "use Void & reissue", one of them wrong
                about what it would do — the page gap says it once and says it correctly. */}
            {!instrumentGap && templateGap && (
                <p className="mt-3 flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                    <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        Template v{templateGap.publishedVersion} has been published since this
                        document was generated on v{templateGap.pinnedVersion}. Documents keep the
                        version they were generated with — use{' '}
                        <span className="font-medium">Void &amp; reissue</span> to move this one
                        onto the current wording.
                    </span>
                </p>
            )}

            {/* THE SPINNER IS THE REQUEST, NOT THE STATE. It shows while a finalize call
                is genuinely out and at no other time. */}
            {sealing && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Finalizing — sealing the signatures and generating the executed PDF.
                </div>
            )}

            {readyToSeal && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                    All parties signed — ready to seal.
                </div>
            )}

            {/* VERBATIM, AND IT STAYS. The renderer names what it is missing — brand
                fonts, a bucket, a frozen body — and only that sentence tells an admin
                whether to fix something or just ask again. */}
            {sealFailed && (
                <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    <p className="flex items-center gap-2 font-medium">
                        <TriangleAlert className="h-4 w-4 shrink-0" />
                        Sealing failed — the executed PDF was not stored.
                    </p>
                    <p className="mt-1 pl-6">{seal.message}</p>
                    {seal.code && (
                        <p className="mt-1 pl-6 font-mono text-xs opacity-80">{seal.code}</p>
                    )}
                </div>
            )}

            {completed && doc?.finalized_at && (
                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                    <span className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Completed · executed PDF ready
                    </span>
                    <Button variant="ghost" size="sm" onClick={actions.onDownloadFinal}>
                        <Download className="mr-2 h-4 w-4" />
                        Download
                    </Button>
                </div>
            )}

            <p className="mt-3 text-xs text-muted-foreground">{statusLine}</p>
        </article>
    );
}
